import styled from 'styled-components'
import { createContext, FC, useContext, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import subMonths from 'date-fns/subMonths'

import { Modal } from 'Components/Modal/Modal'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { ModalHeader } from 'Components/Modal/ModalHeader'

import {
  ConfirmationModalContent,
  ConfirmationModalContextProviderProps,
  useModalShow
} from 'Hooks/modals/useModalShow'

import { useEmployerInfoContext } from 'Contexts/EmployerInfoContext'
import { useUserInfoContext } from 'Contexts/UserInfoContext'
import { getEmployerAdminInvoices } from 'Services/invoices/invoiceService'
import { spacingSM, spacingXS } from 'Utils/styles/spacing'
import { ThinDarkGreenText } from 'Utils/styles/text'
import { createMonthYearOption } from 'Utils/helpers/reports'
import { getGlobalAdminRole } from 'Utils/helpers/user'
import { CountryCode, CountryNames } from 'Utils/types/countries'
import { InvoiceState } from 'Utils/types/invoice'
import { StyledSelect } from 'Components/Inputs/StyledSelect'

const ButtonContainer = styled.div`
  display: flex;
  gap: ${spacingSM};
  justify-content: end;
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacingXS};
`

interface CompletePurchasesReportModalResult {
  date: Date
  countryCode: string
}

interface CompletePurchasesReportModalOptions {
  countries: CountryCode[]
  minDate?: Date
}

interface ModalContextType {
  showModal: (
    options: CompletePurchasesReportModalOptions
  ) => Promise<CompletePurchasesReportModalResult | undefined>
}

const UseCompletePurchasesReportModal = createContext<ModalContextType>({} as ModalContextType)

const FINALIZED_STATES: InvoiceState[] = [
  InvoiceState.Open,
  InvoiceState.Processing,
  InvoiceState.Completed
]

const CompletePurchasesReportModalProvider: FC<ConfirmationModalContextProviderProps> = (props) => {
  const { setShow, show, onHide } = useModalShow()

  const {
    state: { employerId }
  } = useEmployerInfoContext()
  const {
    state: { userRoles }
  } = useUserInfoContext()
  const isAdmin = !!getGlobalAdminRole(userRoles)

  // Determine months with finalized invoices (sc-7309)
  const { data: invoicesData } = useQuery({
    queryKey: ['purchases-report-finalized-months', employerId],
    queryFn: () =>
      getEmployerAdminInvoices({
        employerId: employerId!,
        pageNumber: 1,
        perPage: 10,
        sortBy: 'date',
        sortDirection: 'desc'
      }),
    enabled: !!employerId && isAdmin
  })
  const latestFinalizedMonth = useMemo(() => {
    const invoices = invoicesData?.data?.invoices
    if (!invoices) return undefined

    const finalized = invoices.find((inv) => FINALIZED_STATES.includes(inv.state))
    if (!finalized) return undefined

    return new Date(finalized.dateCreated.getFullYear(), finalized.dateCreated.getMonth(), 1)
  }, [invoicesData])

  const [selectedMonth, setSelectedMonth] = useState<string>('')
  const [selectedCountry, setSelectedCountry] = useState<string>('')
  const [availableCountries, setAvailableCountries] = useState<CountryCode[]>([])
  const [minDate, setMinDate] = useState<Date | undefined>()
  const [content, setContent] = useState<ConfirmationModalContent | null>()
  const resolver =
    useRef<
      (
        value:
          | CompletePurchasesReportModalResult
          | undefined
          | PromiseLike<CompletePurchasesReportModalResult | undefined>
      ) => void
    >()

  const monthOptions = useMemo(() => {
    if (!latestFinalizedMonth) return []

    const options = []
    let reportDate = latestFinalizedMonth

    for (let i = 0; i < 24; i++) {
      if (minDate && reportDate < minDate) break
      options.push(createMonthYearOption(reportDate, false))
      reportDate = subMonths(reportDate, 1)
    }

    return options
  }, [minDate, latestFinalizedMonth])

  const showModal = (
    options: CompletePurchasesReportModalOptions
  ): Promise<CompletePurchasesReportModalResult | undefined> => {
    const { countries, minDate } = options

    setContent({
      title: 'Complete Purchases Report - Select Month',
      message: '',
      confirmButtonId: 'continue.label',
      cancelButtonId: 'cancel.label'
    })

    setAvailableCountries(countries)
    setSelectedCountry(countries[0] || 'US')
    setMinDate(minDate)
    setSelectedMonth('')

    setShow(true)
    return new Promise((resolve) => {
      resolver.current = resolve
    })
  }

  const handleOk = () => {
    if (resolver.current && selectedMonth) {
      const [month, year] = selectedMonth.split('-')
      resolver.current({
        date: new Date(parseInt(year), parseInt(month), 1),
        countryCode: selectedCountry
      })
    }
    onHide()
  }

  const handleCancel = () => {
    if (resolver.current) {
      resolver.current(undefined)
    }
    onHide()
  }

  return (
    <UseCompletePurchasesReportModal.Provider
      value={{
        showModal
      }}
    >
      {props.children}

      {content && (
        <Modal showModal={show} setShowModal={onHide}>
          <ModalHeader>{content.title}</ModalHeader>

          {content.message && <ThinDarkGreenText>{content.message}</ThinDarkGreenText>}

          <ModalContent>
            <StyledSelect
              options={monthOptions}
              handleChange={setSelectedMonth}
              placeholderText="Select a month"
            />

            {availableCountries.length > 1 && (
              <StyledSelect
                options={availableCountries.map((countryCode) => ({
                  label: CountryNames[countryCode],
                  value: countryCode
                }))}
                handleChange={setSelectedCountry}
                placeholderText="Select a country"
                defaultValue={selectedCountry}
              />
            )}
          </ModalContent>

          <ButtonContainer>
            <StyledButton id={content.cancelButtonId} onClick={handleCancel} />
            <StyledButton
              id={content.confirmButtonId}
              primary
              onClick={handleOk}
              disabled={!selectedMonth}
            />
          </ButtonContainer>
        </Modal>
      )}
    </UseCompletePurchasesReportModal.Provider>
  )
}

const useCompletePurchasesReportModal = (): ModalContextType =>
  useContext(UseCompletePurchasesReportModal)

export { useCompletePurchasesReportModal }

export default CompletePurchasesReportModalProvider
